import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import LogoutIcon from '@mui/icons-material/Logout'
import NotificationsIcon from '@mui/icons-material/Notifications'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import UserAvatar from 'components/UserAvatar/UserAvatar'
import { useTheme } from 'context/ThemeContext'
import { useUser } from 'hooks/user'
import { ReactComponent as GearSvg } from 'images/icons/gear.svg'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'

import style from './UserMenu.module.css'

type Props = {
  onRequestClose: () => void
}

const UserMenu = React.forwardRef<HTMLDivElement | null, Props>(({ onRequestClose }, ref) => {
  const { t } = useTranslation()
  const user = useUser()
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()

  const go = useCallback(
    (path: string) => {
      onRequestClose()
      navigate(path)
    },
    [navigate, onRequestClose]
  )

  const onLogoutClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onRequestClose()
      user?.actions.signout()
    },
    [onRequestClose, user]
  )

  const onThemeClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      toggleTheme()
    },
    [toggleTheme]
  )

  return (
    <div ref={ref} className={style.menu} data-test-id={getTestId('userMenu')}>
      <div className={style.userHeader}>
        <UserAvatar
          url={user?.state.avatarUrl}
          firstName={user?.state.firstName}
          lastName={user?.state.lastName}
          email={user?.state.email}
          size={40}
        />
        <div className={style.userName}>
          {[user?.state.firstName, user?.state.lastName].filter(Boolean).join(' ') || user?.state.email}
        </div>
      </div>
      <div className={style.divider} />
      <div
        className={style.item}
        onClick={event => {
          event.stopPropagation()
          go(PATH.ACCOUNT_PROFILE)
        }}
        data-test-id={getTestId('userMenuProfile')}
      >
        <PersonOutlineIcon className={style.itemIcon} />
        {t('account.nav_profile')}
      </div>
      {user?.state.canUseIam ? (
        <div
          className={style.item}
          onClick={event => {
            event.stopPropagation()
            go(PATH.ACCOUNT_USERS)
          }}
          data-test-id={getTestId('userMenuUsers')}
        >
          <PeopleOutlineIcon className={style.itemIcon} />
          {t('account.nav_users')}
        </div>
      ) : null}
      <div
        className={style.item}
        onClick={event => {
          event.stopPropagation()
          go(PATH.ACCOUNT_NOTIFICATIONS)
        }}
        data-test-id={getTestId('userMenuHistory')}
      >
        <NotificationsIcon className={style.itemIcon} />
        {t('header.notifications')}
      </div>
      <div
        className={style.item}
        onClick={event => {
          event.stopPropagation()
          go(PATH.ACCOUNT_NOTIFICATION_SETTINGS)
        }}
        data-test-id={getTestId('userMenuNotificationSettings')}
      >
        <GearSvg className={style.itemIcon} />
        {t('header.settings')}
      </div>
      <div className={style.item} onClick={onThemeClick} data-test-id={getTestId('userMenuTheme')}>
        {theme === 'light' ? (
          <DarkModeIcon className={style.itemIcon} />
        ) : (
          <LightModeIcon className={style.itemIcon} />
        )}
        {theme === 'light' ? t('header.theme_dark') : t('header.theme_light')}
      </div>
      <div className={style.divider} />
      <div className={style.item} onClick={onLogoutClick} data-test-id={getTestId('userMenuLogout')}>
        <LogoutIcon className={style.itemIcon} />
        {t('header.logout')}
      </div>
    </div>
  )
})

export default UserMenu
